/**
 * Consent state management for Google Consent Mode v2.
 *
 * index.html sets the Consent Mode DEFAULT to "denied" for ad_storage,
 * ad_user_data, ad_personalization and analytics_storage on every page load
 * (functionality_storage + security_storage stay granted — essential). This
 * module reads the visitor's stored choice and calls gtag('consent','update')
 * to lift the relevant categories, and persists the choice so it survives
 * reloads.
 *
 * Categories:
 *   - analytics : GA4 measurement cookies (live today).
 *   - ads       : display-advertising cookies (reserved for the August-2026
 *                 ad launch; no ad cookies are set until that ships — the
 *                 AdSlot component is inert until both an ad client and ads
 *                 consent exist).
 *
 * When the certified CMP (Google Privacy & messaging) is enabled for the ad
 * launch, it manages IAB TCF v2.3 consent for EEA/UK personalised ads, and
 * this first-party banner is retired or scoped to non-TCF categories — see
 * .agents/ad-monetisation-readiness.md.
 */

export const CONSENT_STORAGE_KEY = 'yad_consent_v1';
// Fired after a choice is saved, so live components (AdSlot) react without a reload.
export const CONSENT_EVENT = 'yad-consent-change';
// Fired by the footer "Cookie settings" link to re-open the banner.
export const CONSENT_REOPEN_EVENT = 'yad-consent-reopen';

const isBrowser = () => typeof window !== 'undefined';

const gtagConsentUpdate = (choice) => {
  if (!isBrowser() || typeof window.gtag !== 'function') return;
  window.gtag('consent', 'update', {
    analytics_storage: choice.analytics ? 'granted' : 'denied',
    ad_storage: choice.ads ? 'granted' : 'denied',
    ad_user_data: choice.ads ? 'granted' : 'denied',
    ad_personalization: choice.ads ? 'granted' : 'denied',
  });
};

/** The stored consent record, or null if the visitor hasn't chosen yet. */
export const getStoredConsent = () => {
  if (!isBrowser()) return null;
  try {
    const raw = window.localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed.analytics === 'boolean' && typeof parsed.ads === 'boolean') {
      return parsed;
    }
    return null;
  } catch {
    return null;
  }
};

/** True once the visitor has made any explicit choice. */
export const hasConsentChoice = () => getStoredConsent() !== null;

/**
 * Re-apply a previously stored choice to gtag. Call once on app mount: the
 * index.html default resets to "denied" on every load, so a returning visitor
 * who accepted needs the grant re-pushed.
 */
export const applyStoredConsent = () => {
  const stored = getStoredConsent();
  if (stored) gtagConsentUpdate(stored);
  return stored;
};

/**
 * Persist a fresh choice, push it to gtag, and notify listeners so they can
 * react without a reload. `choice` is { analytics, ads }.
 */
export const saveConsent = (choice) => {
  const record = {
    analytics: !!choice.analytics,
    ads: !!choice.ads,
    ts: Date.now(),
    v: 1,
  };
  if (isBrowser()) {
    try {
      window.localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(record));
    } catch {
      /* storage blocked (private mode / quota) — choice still applies this page */
    }
  }
  gtagConsentUpdate(record);
  if (isBrowser()) {
    window.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail: record }));
  }
  return record;
};

export const acceptAll = () => saveConsent({ analytics: true, ads: true });
export const rejectNonEssential = () => saveConsent({ analytics: false, ads: false });

/** AdSlot gate: are advertising cookies allowed right now? */
export const adsConsentGranted = () => !!getStoredConsent()?.ads;
/** Analytics gate (for future use; GA4 itself respects Consent Mode directly). */
export const analyticsConsentGranted = () => !!getStoredConsent()?.analytics;
